"use client";

import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Shield, Award, Star, Sparkles, BadgeDollarSign, CheckCircle2, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRef } from "react";

// Sparkle animation component
function AnimatedSparkle({ delay, className }: { delay: number; className: string }) {
  return (
    <motion.div
      className={cn("absolute", className)}
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: [0, 1, 0],
        scale: [0, 1, 0],
        rotate: [0, 180, 360],
      }}
      transition={{
        duration: 2,
        delay,
        repeat: Infinity,
        repeatDelay: 3,
      }}
    >
      <Sparkles className="w-4 h-4 text-amber-400" />
    </motion.div>
  );
}

// Animated gradient orb
function GradientOrb({ className, delay = 0 }: { className: string; delay?: number }) {
  return (
    <motion.div
      className={cn("absolute rounded-full blur-3xl", className)}
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3],
        x: [0, 30, 0],
        y: [0, -20, 0],
      }}
      transition={{
        duration: 8,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

// Animated text reveal with gradient
function GradientTextReveal({ 
  text, 
  className,
  delay = 0,
  isAccent = false 
}: { 
  text: string; 
  className?: string;
  delay?: number;
  isAccent?: boolean;
}) {
  return (
    <motion.span
      className={cn(
        "inline-block",
        isAccent && "bg-gradient-to-r from-primary via-amber-500 to-primary bg-clip-text text-transparent bg-[length:200%_auto]",
        className
      )}
      initial={{ opacity: 0, y: 50, rotateX: -90 }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        rotateX: 0,
        backgroundPosition: isAccent ? ["0% center", "200% center"] : undefined,
      }}
      transition={{
        opacity: { duration: 0.6, delay },
        y: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
        rotateX: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
        backgroundPosition: isAccent ? {
          duration: 3,
          delay: delay + 0.5,
          repeat: Infinity,
          ease: "linear",
        } : undefined,
      }}
    >
      {text}
    </motion.span>
  );
}

// Animated line/underline
function AnimatedUnderline({ delay = 0 }: { delay?: number }) {
  return (
    <motion.div
      className="h-1 bg-gradient-to-r from-primary via-amber-500 to-primary rounded-full mt-4"
      initial={{ scaleX: 0, opacity: 0 }}
      animate={{ scaleX: 1, opacity: 1 }}
      transition={{
        duration: 1,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  );
}

// Feature pill component
function FeaturePill({ 
  icon: Icon, 
  text, 
  delay 
}: { 
  icon: typeof Shield; 
  text: string; 
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{ scale: 1.05, y: -2 }}
      className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/80 backdrop-blur-sm border border-border/50 shadow-lg"
    >
      <Icon className="w-4 h-4 text-primary" />
      <span className="text-sm font-medium text-foreground">{text}</span>
    </motion.div>
  );
}

// Floating icon with glow
function FloatingIconWithGlow({ 
  icon: Icon, 
  position, 
  delay,
  color = "primary"
}: { 
  icon: typeof Shield; 
  position: string; 
  delay: number;
  color?: string;
}) {
  const colorClasses = {
    primary: "text-primary shadow-primary/30",
    amber: "text-amber-500 shadow-amber-500/30",
    emerald: "text-emerald-500 shadow-emerald-500/30",
  };
  
  return (
    <motion.div
      className={cn("absolute hidden lg:flex items-center justify-center", position)}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay }}
    >
      <motion.div
        className={cn(
          "relative p-4 rounded-2xl bg-card/50 backdrop-blur-md border border-border/30",
          "shadow-2xl",
          colorClasses[color as keyof typeof colorClasses]
        )}
        animate={{
          y: [0, -10, 0],
          rotate: [0, 5, 0, -5, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay,
        }}
      >
        {/* Glow effect */}
        <motion.div
          className={cn(
            "absolute inset-0 rounded-2xl blur-xl opacity-50",
            color === "primary" && "bg-primary",
            color === "amber" && "bg-amber-500",
            color === "emerald" && "bg-emerald-500"
          )}
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <Icon className={cn("w-8 h-8 relative z-10", colorClasses[color as keyof typeof colorClasses].split(" ")[0])} />
      </motion.div>
    </motion.div>
  );
}

export function ValueProposition() {
  const { t, i18n } = useTranslation("home");
  const isRTL = i18n.language === "he";
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const features = isRTL 
    ? ["מחיר הוגן", "איכות מקצועית", "שירות אישי", "תוצאות מוכחות"]
    : ["Fair Pricing", "Expert Quality", "Personal Service", "Proven Results"];

  return (
    <section 
      ref={sectionRef}
      className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-b from-background via-muted/20 to-background"
    >
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0 opacity-30"
        style={{
          background: "radial-gradient(ellipse at center, hsl(var(--primary) / 0.15) 0%, transparent 70%)",
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.35, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Gradient orbs */}
      <GradientOrb className="w-[800px] h-[800px] -top-40 -left-40 bg-primary/20" delay={0} />
      <GradientOrb className="w-[600px] h-[600px] -bottom-20 -right-20 bg-amber-500/15" delay={2} />
      <GradientOrb className="w-[400px] h-[400px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary/10" delay={4} />

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: "50px 50px",
        }}
      />

      {/* Sparkles */}
      {isInView && (
        <>
          <AnimatedSparkle delay={0.5} className="top-20 left-[20%]" />
          <AnimatedSparkle delay={1.2} className="top-32 right-[25%]" />
          <AnimatedSparkle delay={2} className="bottom-32 left-[30%]" />
          <AnimatedSparkle delay={2.8} className="bottom-20 right-[20%]" />
          <AnimatedSparkle delay={1.8} className="top-1/2 left-[10%]" />
          <AnimatedSparkle delay={3.2} className="top-1/2 right-[15%]" />
        </>
      )}

      {/* Floating icons with glow */}
      {isInView && (
        <>
          <FloatingIconWithGlow icon={Shield} position="top-24 left-[8%]" delay={0.3} color="primary" />
          <FloatingIconWithGlow icon={Award} position="top-32 right-[12%]" delay={0.6} color="amber" />
          <FloatingIconWithGlow icon={Zap} position="bottom-28 left-[12%]" delay={0.9} color="amber" />
          <FloatingIconWithGlow icon={CheckCircle2} position="bottom-24 right-[8%]" delay={1.2} color="emerald" />
        </>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div 
          className="text-center max-w-5xl mx-auto"
          dir={isRTL ? "rtl" : "ltr"}
        >
          {/* Badge */}
          {isInView && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-10 inline-block"
            >
              <motion.span 
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/20 via-primary/10 to-amber-500/20 text-primary text-sm font-semibold border border-primary/30 shadow-lg shadow-primary/10"
                animate={{
                  boxShadow: [
                    "0 10px 40px -10px hsl(var(--primary) / 0.2)",
                    "0 10px 40px -10px hsl(var(--primary) / 0.4)",
                    "0 10px 40px -10px hsl(var(--primary) / 0.2)",
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                >
                  <Star className="w-4 h-4 fill-current" />
                </motion.div>
                {isRTL ? "הצעת הערך שלנו" : "Our Value Proposition"}
              </motion.span>
            </motion.div>
          )}

          {/* Main Headlines with dramatic reveal */}
          <div className="space-y-4 mb-10 perspective-1000">
            {isInView && (
              <>
                <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight overflow-hidden">
                  <GradientTextReveal 
                    text={t("valueProposition.headline1")} 
                    delay={0.2}
                  />
                </h2>
                
                <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight overflow-hidden">
                  <GradientTextReveal 
                    text={t("valueProposition.headline2")} 
                    delay={0.5}
                    isAccent
                  />
                </h2>
              </>
            )}
          </div>

          {/* Animated underline */}
          {isInView && (
            <div className="flex justify-center mb-12">
              <div className="w-32 md:w-48">
                <AnimatedUnderline delay={1} />
              </div>
            </div>
          )}

          {/* Subheadlines with stagger */}
          {isInView && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="space-y-4 max-w-3xl mx-auto mb-14"
            >
              <motion.p 
                className="text-xl md:text-2xl text-muted-foreground leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 0.6 }}
              >
                {t("valueProposition.subheadline")}
              </motion.p>
              <motion.p 
                className="text-lg md:text-xl text-foreground/80 leading-relaxed font-medium"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.6 }}
              >
                {t("valueProposition.subheadlineLine2")}
              </motion.p>
            </motion.div>
          )}

          {/* Feature pills */}
          {isInView && (
            <motion.div 
              className="flex flex-wrap justify-center gap-3 md:gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8 }}
            >
              {features.map((feature, index) => (
                <FeaturePill
                  key={feature}
                  icon={[Shield, Award, Star, CheckCircle2][index]}
                  text={feature}
                  delay={2 + index * 0.15}
                />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
